import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { Loader } from "@themes/pollinator/ui/Loader";
import { AppPageHeader } from "@themes/pollinator/ui/AppPageHeader";
import {
  useFetchActions,
  ACTION_TYPE_SLUG,
  type Action,
} from "@core/features/actions";
import { usePlatformStore } from "@platform/store";
import {
  InvitationLinkSection,
  InvitationFormContainer,
  InvitationRecordsTableContainer,
} from "@platform/features/invitation";
import Type from "@themes/pollinator/ui/Type";

type InvitationFromAction = {
  id: number;
  shortcode: string;
  name?: string;
  max_uses?: number;
};

export function InvitationsPage() {
  const { id: programId } = useParams<{ id: string }>();
  const user = usePlatformStore((s) => s.user);

  const { data: actionsData, isLoading } = useFetchActions({
    programId: Number(programId),
    user,
    isEnabled: !!user && !!programId,
  });

  // Find the send_invitation action for this program
  const inviteFriendsAction = useMemo(() => {
    if (!actionsData?.actions) return null;
    return actionsData.actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.SEND_INVITATION
    );
  }, [actionsData?.actions]);

  // Get the invitation from the action's target_object_data
  const invitation = useMemo(() => {
    if (!inviteFriendsAction?.target_object_data) return null;
    const data = inviteFriendsAction.target_object_data as InvitationFromAction;
    if (!data.id || !data.shortcode) return null;
    return data;
  }, [inviteFriendsAction]);

  return (
    <>
      <AppPageHeader
        title="Invitations"
        description="Invite friends to join Pollinator, and then track your sent invitations."
      />
      <OuterContentContainer className="flex flex-1" innerClassName="gap-8">
        <div className="w-full flex flex-col gap-6 pb-8 max-w-3xl">
          {isLoading ? (
            <Loader />
          ) : !invitation ? (
            <Type variant="body2" className="text-brand-700">
              No invitation available for this program.
            </Type>
          ) : (
            <div className="flex flex-col gap-12">
              <div className="flex flex-col gap-4">
                <InvitationFormContainer
                  invitationId={invitation.id}
                  maxUses={invitation.max_uses}
                />
                <InvitationRecordsTableContainer invitationId={invitation.id} />
              </div>
              <InvitationLinkSection shortcode={invitation.shortcode} maxUses={invitation.max_uses} />
            </div>
          )}
        </div>
      </OuterContentContainer>
    </>
  );
}

export default InvitationsPage;
